import React from 'react';
import { Helmet } from 'react-helmet';
import { buildTitle, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seo';

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>{buildTitle('Privacy Policy')}</title>
        <meta
          name="description"
          content="Privacy policy for Sista Kyerewaa, including GDPR data use, cookies, analytics, and third-party services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/privacy`} />
        <meta property="og:title" content={buildTitle('Privacy Policy')} />
        <meta property="og:description" content="Privacy policy for Sista Kyerewaa, including GDPR data use, cookies, analytics, and third-party services." />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={buildTitle('Privacy Policy')} />
        <meta property="twitter:description" content="Privacy policy for Sista Kyerewaa, including GDPR data use, cookies, analytics, and third-party services." />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <link rel="canonical" href={`${SITE_URL}/privacy`} />
      </Helmet>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[#7b7671]">Legal</p>
          <h1 className="font-display text-4xl md:text-5xl mt-4 text-[#1c1b1a]">Privacy Policy</h1>
          <p className="text-[#6a6661] mt-4">
            This policy explains how SistaKyerewaa collects, uses, and protects personal data in accordance with EU/GDPR requirements.
          </p>
        </div>

        <section className="space-y-10 text-[#2d2b29]">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">
            Last updated: February 4, 2026
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="border border-stone-200 bg-white/70 p-6">
            <h2 className="font-display text-2xl mb-2">Controller</h2>
            <p className="text-[#6a6661]">
              SistaKyerewaa (replace with legal entity if different)
              <br />
              Email: hello@sistakyerewaa.com
              <br />
              Address: [Add your business address]
            </p>
          </div>
          <div className="border border-stone-200 bg-white/70 p-6">
            <h2 className="font-display text-2xl mb-2">Scope</h2>
            <p className="text-[#6a6661]">
              This policy covers the personal data collected through this website, including the blog,
              store features, and any contact or booking links.
            </p>
          </div>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">What We Collect</h2>
          <p className="text-[#6a6661]">
            We only collect data needed to run the site and respond to you.
          </p>
          <ul className="list-disc pl-6 text-[#6a6661]">
            <li>Technical data (IP address, device and browser info, access time).</li>
            <li>Usage data (pages visited, clicks, scrolls, and interactions).</li>
            <li>Contact data you send us (email, messages, or booking requests).</li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Why We Use It</h2>
          <ul className="list-disc pl-6 text-[#6a6661]">
            <li>To deliver the site, content, and requested services.</li>
            <li>To improve performance and user experience.</li>
            <li>To answer messages, inquiries, or booking requests.</li>
            <li>To measure which content helps our community most.</li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Legal Bases</h2>
          <ul className="list-disc pl-6 text-[#6a6661]">
            <li>Consent (e.g., for cookies or marketing, where required).</li>
            <li>Performance of a contract or pre‑contractual steps.</li>
            <li>Legitimate interests (site security, improvements, analytics).</li>
            <li>Legal obligations where applicable.</li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Hosting & Performance</h2>
          <p className="text-[#6a6661]">
            This website is hosted on Vercel. We use Vercel Speed Insights to understand
            performance. Speed Insights collects anonymous performance data and does not identify
            individual visitors.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Analytics & Advertising</h2>
          <p className="text-[#6a6661]">
            We use Google Analytics to understand how visitors use the site and to improve
            content. Google Analytics may set cookies or similar technologies to collect
            usage data.
          </p>
          <p className="text-[#6a6661] mt-4">
            We have not enabled Google AdSense yet. If we do, ads may use cookies or similar
            technologies to personalize ads and measure performance.
          </p>
          <p className="text-[#6a6661] mt-4">
            In the EEA, UK, and Switzerland, Google requires consent for cookies/local storage
            and for personalized ads.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Affiliate Links</h2>
          <p className="text-[#6a6661]">
            Some links are affiliate or referral links (for example to money transfer providers
            and banks such as LemFi and N26). If you click or sign up through them, we may receive a
            commission or reward at no extra cost to you. Posts that contain such links are marked
            "Werbung / Advertisement". The partner may receive information about the referral, which
            can involve tracking parameters, and may set cookies under its own privacy policy.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Third‑Party Links</h2>
          <p className="text-[#6a6661]">
            We link to third‑party services such as TikTok, Instagram, Facebook, Linktree,
            and Chatydesk (our booking provider). When you click, those providers process data under their own policies.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">International Transfers</h2>
          <p className="text-[#6a6661]">
            Some service providers process data outside the EU/EEA. Where required, we rely on
            appropriate safeguards such as Standard Contractual Clauses.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Your Rights</h2>
          <ul className="list-disc pl-6 text-[#6a6661]">
            <li>Right to be informed, access, and rectification.</li>
            <li>Right to erasure, restriction, and data portability.</li>
            <li>Right to object and to withdraw consent at any time.</li>
            <li>Right to lodge a complaint with a supervisory authority.</li>
          </ul>
          <p className="text-[#6a6661] mt-3">
            In Germany, the competent authority depends on the controller. The BfDI handles
            certain federal bodies, while state authorities handle most private companies.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Retention</h2>
          <p className="text-[#6a6661]">
            We keep personal data only as long as needed for the purposes described or as required by law.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-2">Updates</h2>
          <p className="text-[#6a6661]">
            We may update this policy to reflect legal or operational changes. The current version
            will always be available on this page.
          </p>
        </div>

        <div className="border border-stone-200 bg-white/70 p-5">
          <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">
            If you need help updating legal details (address, company name), contact us at hello@sistakyerewaa.com.
          </p>
        </div>
        </section>
      </main>
    </>
  );
}
